import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { Grid, Stack } from "@mui/material";
import { Icons, Alert, Box, Button, Card, DateField, Divider, Form, IconButton, MenuItem, SelectField, TextField, Typography } from "@curves/ui-base";
import { OrderInvoiceSection, NewOrderCart, UnpaidOrderList, QuestionnaireBox, BatchDeviceOrderCart } from "@icoach/accountBook/createOrder/components/";
import useDeviceOrderFeeSync from "@icoach/accountBook/createOrder/hooks/useDeviceOrderFeeSync";
import { ensureArray, formatCurrencyFn, isEmpty, refIsRequiredError } from "@util/utils";
import { EmployeeSelect } from "@icoach/components";
import { getFirstOfMonth, getNowTime, isBefore, parseDate } from "@util/moment";
import { Link } from "react-router-dom";
import { ACCOUNT_BOOK_ORDERS_OVERVIEW_URL } from "@icoach/router/routerPath";
import usePriceSummary from "@icoach/accountBook/createOrder/hooks/usePriceSummary";
import useOrderCampaignDiscount from "@icoach/accountBook/createOrder/hooks/useOrderCampaignDiscount";
import { calculateOrderTotalAmount } from "@icoach/accountBook/createOrder/CreateOrderPage.util";
import useMessageDialog from "@util/hook/useMessageDialog";
import { useSnackbar } from "notistack";
import clsx from "clsx";

/**
 * 結帳內容區塊
 * 整合購物車、欠費訂單、發票、問卷、付款資訊等組件
 * 負責處理表單提交邏輯
 */
const CheckoutContent = ({ className, receiverTypeRef, receiverFieldRef, receiverType, checkoutInfo, options, submit, validationErrors, clearValidationErrors }) => {
	// ==================== Props 解構和基本變數 ====================
	const { campaignOptions = [] } = options || {};
	const { unpaidOrders = [], invoiceSetting = {}, member = {}, checkUsingDeviceOrder } = checkoutInfo;
	const isShowUnpaidOrders = !isEmpty(unpaidOrders);
	const isUsingDeviceOrder = checkUsingDeviceOrder || false;

	// ==================== Refs ====================
	const newOrderCartRef = useRef(null);
	const unpaidOrderRef = useRef(null);
	const questionnaireRef = useRef(null);
	const invoiceRef = useRef(null);
	const employeeRef = useRef(null);
	const orderDateRef = useRef(null);
	const campaignRef = useRef(null);
	const cashPaymentRef = useRef(null);
	const cardPaymentRef = useRef(null);
	const batchDeviceOrderCartRef = useRef(null);

	// ==================== Hooks ====================
	const { enqueueSnackbar } = useSnackbar();
	const setMessageDialog = useMessageDialog();

	// 訂單總額狀態（不包含智能月費，智能月費是總部收款）
	const [orderTotal, setOrderTotal] = useState(0);

	// 問卷顯示條件：來自購物車的觸發
	const [isCartQuestionnaireEligible, setCartQuestionnaireEligible] = useState(false);

	// 抵用劵選擇狀態
	const [selectedCampaign, setSelectedCampaign] = useState(null);

	// 抵用劵折扣資料 Hook
	const { campaignResult, fetchCampaignDiscount, clearCampaignResult } = useOrderCampaignDiscount();

	// 使用智能月費同步 Hook，智能月費為獨立批次扣款
	const { deviceOrderCartItems, deviceOrderCartTotal, wrapUpdatePrice, wrapDeleteItem } = useDeviceOrderFeeSync({
		memberID: member?.memberID,
		isUsingDeviceOrder,
	});

	// ==================== 計算值和衍生狀態 ====================
	// 訂單總金額 (從 campaignResult 取得)
	const orderSummary = useMemo(
		() => {
			if (isEmpty(campaignResult)) return orderTotal;
			return Number(campaignResult.totalPrice) || 0;
		},
		// eslint-disable-next-line
		[orderTotal, campaignResult],
	)
	
	
	// 折抵金額（從 campaignResult 取得）
	const discountAmount = useMemo(
		() => {
			if (isEmpty(campaignResult)) return 0;
			return Number(campaignResult.discountTotalPrice) || 0;
		},
		// eslint-disable-next-line
		[campaignResult],
	);

	// 折抵後應付金額（orderTotal - discountAmount，不得為負）
	const payableAmount = useMemo(
		() => {
			const result = Number(orderSummary) - Number(discountAmount);
			return result > 0 ? result : 0;
		},
		// eslint-disable-next-line
		[orderSummary, discountAmount],
	);

	// 使用價格摘要 Hook（基準為折抵後金額）
	const { cashPayment, cardPayment, paidAmount, balanceStatus, onCashPaymentChange, onCardPaymentChange, syncFromRefs, getPaymentResults, getPaymentMatchStatus } = usePriceSummary({
		orderTotal: payableAmount,
		refs: {
			cashPaymentRef,
			cardPaymentRef,
		},
	});

	// ==================== 業務邏輯函數 ====================
	/**
	 * 計算訂單總額並更新狀態
	 * 這個方法會被購物車和賒帳訂單組件調用
	 */
	const calculateOrderTotal = useCallback(
		() => {
			const refs = {
				newOrderCartRef,
				unpaidOrderRef,
			};
			const total = calculateOrderTotalAmount(refs);
			setOrderTotal(total);
			return total;
		},
		// eslint-disable-next-line
		[],
	);

	/**
	 * 收集所有表單參數
	 * @returns {Object} 表單參數物件
	 */
	const getParams = useCallback(
		() => {
			let result = {};

			// 擔當
			if (employeeRef?.current?.getResult) {
				result.employeeID = employeeRef.current.getResult() || 0;
			}

			// 建立日期
			if (orderDateRef?.current?.getResult) {
				result.orderDate = orderDateRef.current.getResult();
			}

			// 購買人資訊
			if (receiverTypeRef?.current?.getResult) {
				result.receiverType = receiverTypeRef.current.getResult();
			}
			if (receiverFieldRef?.current?.getResult) {
				result = Object.assign(result, receiverFieldRef.current.getResult());
			}

			// 新訂單購物車資料（主要購物車）
			if (newOrderCartRef?.current?.getResult) {
				result = Object.assign(result, newOrderCartRef.current.getResult());
			}

			// 賒帳訂單資料（未結清的過往訂單）
			// 注意：賒帳訂單不在折抵範圍內，直接使用原始資料
			if (unpaidOrderRef?.current?.getResult) {
				const { checkoutOrderIDs } = unpaidOrderRef.current.getResult();
				result.checkoutOrderIDs = checkoutOrderIDs;
			} else {
				result.checkoutOrderIDs = [];
			}

			// 問卷資料
			if (questionnaireRef?.current?.getResult) {
				const { purchaseTimeType, purchaseToolType } = questionnaireRef.current.getResult();
				result.purchaseTimeType = purchaseTimeType || 0;
				result.purchaseToolType = purchaseToolType || 0;
			} else {
				result.purchaseTimeType = 0;
				result.purchaseToolType = 0;
			}

			// 發票設定
			if (invoiceRef?.current?.getResult) {
				result.invoiceSetting = invoiceRef.current.getResult();
			}

			// 批次智能訂單資料（總部收款）
			if (batchDeviceOrderCartRef?.current?.getResult) {
				result.deviceOrderItems = batchDeviceOrderCartRef.current.getResult();
			}

			// 抵用劵 ID（如果有選擇）
			if (selectedCampaign) {
				result.campaignID = selectedCampaign;
			}

			// 折抵金額，後端比對用
			if (!isEmpty(campaignResult)) {
				result.discountTotalPrice = Number(campaignResult.discountTotalPrice) || 0;
			}

			// 付款資訊
			const paymentResults = getPaymentResults();
			result = Object.assign(result, paymentResults);

			return result;
		},
		// eslint-disable-next-line
		[selectedCampaign, getPaymentResults, campaignResult],
	);

	// ==================== 事件處理函數 ====================
	/**
	 * 處理抵用劵選擇變更
	 */
	const handleChangeCampaign = (e, n, value) => {
		setSelectedCampaign(value || null);
	};

	/**
	 * 清除抵用劵選擇
	 */
	const handleClearCampaign = () => {
		setSelectedCampaign(null);
		clearCampaignResult();
	};

	/**
	 * 重新取得折扣資訊
	 */
	const refreshCampaignDiscount = useCallback(
		async () => {
			if (!selectedCampaign) {
				clearCampaignResult();
				return;
			}
			const params = { ...getParams(), campaignID: selectedCampaign };
			await fetchCampaignDiscount(params);
		},
		// eslint-disable-next-line
		[selectedCampaign, fetchCampaignDiscount, getParams, clearCampaignResult],
	);

	// ==================== 驗證函數 ====================
	/**
	 * 檢查表單錯誤
	 * @returns {boolean} 是否有錯誤
	 */
	const checkError = () => {
		// 先同步輸入框的值到 state（處理使用者直接按 Enter 的情況）
		syncFromRefs();

		let isError = false;

		// 檢查必填欄位
		if (!isError) {
			isError = refIsRequiredError(employeeRef, orderDateRef, receiverTypeRef, receiverFieldRef, newOrderCartRef, unpaidOrderRef, questionnaireRef, invoiceRef, batchDeviceOrderCartRef, cashPaymentRef, cardPaymentRef);
		}

		// 檢查建立日期不可早於本月初
		if (orderDateRef?.current?.getResult) {
			if (isBefore(orderDateRef.current.getResult(), getFirstOfMonth(parseDate()))) {
				enqueueSnackbar("依據會計原則不修改歷史報表，請在本月調整！", { variant: "error" });
				return true;
			}
		}

        const paymentValidation = getPaymentMatchStatus();
		if (!paymentValidation.isValid) {
			enqueueSnackbar(paymentValidation.message, { variant: "error" });
			return true;
		}

		return isError;
	};

	/**
	 * 處理結帳送出
	 */
	const handleFormSubmit = () => {
		clearValidationErrors();
		const isError = checkError();

		if (!isError) {
			// 同步輸入框的值
			syncFromRefs();

			// 檢查收款金額是否需要確認
			const paymentValidation = getPaymentMatchStatus();
			if (paymentValidation.needsConfirmation) {
				// 需要確認，開啟確認對話框
				setMessageDialog({
					open: true,
					title: "確認建立欠費訂單",
					msg: paymentValidation.message,
					onConfirm: () => {
						const params = getParams();
						submit(params);
					},
				});
			} else {
				// 不需要確認，直接送出
				const params = getParams();
				submit(params);
			}
		}
	};

	// ==================== Effect Hooks ====================
	// 初始計算訂單總額
	useEffect(
		() => {
			calculateOrderTotal();
		},
		// eslint-disable-next-line
		[],
	);

	// 當折抵相關依賴變更時重新請求折扣資訊
	useEffect(
		() => {
			refreshCampaignDiscount();
		},
		// eslint-disable-next-line
		[selectedCampaign, orderTotal],
	);

	return (
		<Form onSubmit={handleFormSubmit}>
			<Box className={className}>
				<Grid spacing={3} container>
					{/* 賒帳清單 */}
					{isShowUnpaidOrders && (
						<Grid xs={12} item>
							<UnpaidOrderList ref={unpaidOrderRef} orders={unpaidOrders} updateOrderTotal={calculateOrderTotal} />
						</Grid>
					)}
					{/* 店鋪現場收入 (新增收入) */}
					<Grid xs={12} item>
						<NewOrderCart
							className="mb-1"
							ref={newOrderCartRef}
							resource={options}
							getReceiver={() => receiverType}
							setShowQuestionnaireBox={setCartQuestionnaireEligible}
							updateOrderTotal={calculateOrderTotal}
							memberID={checkoutInfo?.member?.memberID}
							checkUsingDeviceOrder={isUsingDeviceOrder}
							wrapUpdatePrice={wrapUpdatePrice}
							wrapDeleteItem={wrapDeleteItem}
						/>
					</Grid>
					{/* 批次智能訂單購物車（總部收款，不計入店鋪 orderTotal） */}
					{!isEmpty(deviceOrderCartItems) && (
						<Grid xs={12} item>
							<BatchDeviceOrderCart ref={batchDeviceOrderCartRef} className="mb-1" deviceOrderCartItems={deviceOrderCartItems} deviceOrderCartTotal={deviceOrderCartTotal} />
						</Grid>
					)}
					<Grid xs={6} item>
						{isCartQuestionnaireEligible && <QuestionnaireBox ref={questionnaireRef} className="mb-4" resource={options} />}
						<Card>
							<Card.CardContent className="p-3">
								<Grid spacing={2} container>
									<Grid xs={12} item>
										<OrderInvoiceSection
											ref={invoiceRef}
											sourceData={{ ...invoiceSetting, issuingEmail: member?.issuingEmail || "" }}
											options={options}
											errorModule={validationErrors}
										/>
									</Grid>
								</Grid>
							</Card.CardContent>
						</Card>
					</Grid>
					<Grid xs={6} item>
						<Card className="mb-2">
							<Card.CardContent className="p-4">
								<Grid container spacing={2}>
									<Grid xs={12} item>
										<Box className="price-summary-section">
											{/* 抵用劵 */}
											<Stack className="mb-2" direction="row" justifyContent="space-between" alignItems="center">
												<Box className="font-color-3">抵用劵</Box>
												<Stack direction="row" spacing={1} alignItems="center" className="price-summary-input-wrap campaign-select-wrap">
													{isEmpty(campaignOptions) ? (
														<SelectField displayEmpty fullWidth>
															<MenuItem disabled value="">
																<em>無抵用劵可使用</em>
															</MenuItem>
														</SelectField>
													) : (
														<>
															<SelectField ref={campaignRef} value={selectedCampaign ?? ""} displayEmpty fullWidth onChange={handleChangeCampaign}>
																<MenuItem disabled value="">
																	<em>請選擇</em>
																</MenuItem>
																{ensureArray(campaignOptions).map(({ value, text }) => (
																	<MenuItem key={value} value={value}>
																		{text}
																	</MenuItem>
																))}
															</SelectField>
															{Boolean(selectedCampaign) && (
																<IconButton className="campaign-select-clear" onClick={handleClearCampaign}>
																	<Icons.Close />
																</IconButton>
															)}
														</>
													)}
												</Stack>
											</Stack>

											{/* 訂單總計 */}
											<Stack className="mb-2" direction="row" justifyContent="space-between" alignItems="center">
												<Box className={clsx("font-color-3", { "font-weight-medium": !Boolean(selectedCampaign), "font-color-3": Boolean(selectedCampaign) })}>訂單總計</Box>
												<Typography variant="body1" className={clsx("mb-0 ", { "font-weight-medium": !Boolean(selectedCampaign), "font-color-3": Boolean(selectedCampaign) })}>
													{formatCurrencyFn(orderSummary, { symbol: "$" })}
												</Typography>
											</Stack>
											{selectedCampaign && (
												<>
													{/* 折抵金額 - 只有選擇抵用劵時才顯示 */}
													<Stack className="mb-2" direction="row" justifyContent="space-between" alignItems="center">
														<Box className="font-color-3">折抵金額</Box>
														<Typography variant="body1" className="mb-0 text-error">
															-{formatCurrencyFn(discountAmount, { symbol: "$" })}
														</Typography>
													</Stack>
													{/* 總收款金額 */}
													<Stack className="mb-2" direction="row" justifyContent="space-between" alignItems="center">
														<Box className="font-weight-medium">總收款金額</Box>
														<Typography variant="body1" className="mb-0 font-weight-medium">
															{formatCurrencyFn(payableAmount, { symbol: "$" })}
														</Typography>
													</Stack>
												</>
											)}

											{/* 實收金額 */}
											<Stack className="mb-2" direction="row" justifyContent="space-between" alignItems="center">
												<Box className="font-color-3">實收金額</Box>
												<Typography variant="body1" className="mb-0 font-color-3">
													{formatCurrencyFn(paidAmount, { symbol: "$" })}
												</Typography>
											</Stack>

											{/* 狀態提示 */}
											{balanceStatus && (
												<Alert severity={balanceStatus.severity} color={balanceStatus.color} variant="filled" className="mb-4">
													<Typography variant="body1" className="mb-0 font-weight-bold">
														{balanceStatus.message}
													</Typography>
												</Alert>
											)}

											{/* 現金收款金額 */}
											<Stack className="my-4" direction="row" justifyContent="space-between" alignItems="center">
												<Box>現金收款金額</Box>
												<Box className="price-summary-input-wrap">
													<TextField
														ref={cashPaymentRef}
														key={`cash-${payableAmount}`}
														defaultValue={cashPayment}
														maskType={"MONEY"}
														inputProps={{ placeholder: "請輸入金額", min: 0 }}
														onChangeNative={onCashPaymentChange}
														fullWidth
														required
													/>
												</Box>
											</Stack>

											{/* 信用卡收款金額 */}
											<Stack className="price-summary-item" direction="row" justifyContent="space-between" alignItems="center">
												<Box>信用卡收款金額</Box>
												<Box className="price-summary-input-wrap">
													<TextField
														ref={cardPaymentRef}
														key={`card-${payableAmount}`}
														defaultValue={cardPayment}
														maskType={"MONEY"}
														inputProps={{ placeholder: "請輸入金額", min: 0 }}
														onChangeNative={onCardPaymentChange}
														fullWidth
														required
													/>
												</Box>
											</Stack>
										</Box>
									</Grid>
									<Grid xs={12} item>
										<Divider className="mb-2" />
									</Grid>
									<Grid xs={6} item>
										<EmployeeSelect ref={employeeRef} options={options?.coachOptions ?? []} label="擔當" fullWidth required />
									</Grid>
									<Grid xs={6} item>
										<DateField label="建立日期" ref={orderDateRef} defaultValue={parseDate()} minDate={getFirstOfMonth(getNowTime())} maxDate={getNowTime()} fullWidth required />
									</Grid>
								</Grid>
							</Card.CardContent>
						</Card>

						<Stack direction="row" spacing={2} justifyContent="end" alignItems="center">
							<Button variant="outlined" component={Link} to={ACCOUNT_BOOK_ORDERS_OVERVIEW_URL}>
								取消，回列表
							</Button>
							<Button type="submit" variant="contained">
								確認結帳
							</Button>
						</Stack>
					</Grid>
				</Grid>
			</Box>
		</Form>
	);
};

export default CheckoutContent;
